import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { BooleanFlag, FeatureFlags, VariantFlag } from "./FeatureFlags";
import { fliptService, FliptService } from "./FliptService";

export { FeatureFlags } from "./FeatureFlags";
export type { BooleanFlag, VariantFlag } from "./FeatureFlags";

// How often the provider asks the SDK to refresh its snapshot. refresh() is ETag-based,
// so unchanged polls are cheap 304s; consumers only re-render when values actually change.
const REFRESH_INTERVAL_MS = 60000;

interface FlagValues {
  booleans: Record<string, boolean>;
  variants: Record<string, string>;
}

function defaultValues(): FlagValues {
  const values: FlagValues = { booleans: {}, variants: {} };
  for (const flag of Object.values(FeatureFlags)) {
    if (flag.type === "boolean") {
      values.booleans[flag.key] = flag.defaultValue;
    } else {
      values.variants[flag.key] = flag.defaultVariant;
    }
  }
  return values;
}

function evaluateAll(service: FliptService): FlagValues {
  const values: FlagValues = { booleans: {}, variants: {} };
  for (const flag of Object.values(FeatureFlags)) {
    if (flag.type === "boolean") {
      values.booleans[flag.key] = service.evaluateBoolean(flag.key, flag.defaultValue);
    } else {
      values.variants[flag.key] = service.evaluateVariant(flag.key, flag.defaultVariant);
    }
  }
  return values;
}

function sameValues(left: FlagValues, right: FlagValues): boolean {
  const booleansEqual = Object.keys(left.booleans).every((key) => left.booleans[key] === right.booleans[key]);
  const variantsEqual = Object.keys(left.variants).every((key) => left.variants[key] === right.variants[key]);
  return booleansEqual && variantsEqual;
}

const FeatureFlagContext = createContext<FlagValues>(defaultValues());

export const FeatureFlagProvider: React.FC<{ children: ReactNode; service?: FliptService }> = ({ children, service = fliptService }) => {
  const [values, setValues] = useState<FlagValues>(defaultValues);

  useEffect(() => {
    let active = true;
    const applyEvaluations = () => {
      if (!active) {
        return;
      }
      const next = evaluateAll(service);
      setValues((previous) => (sameValues(previous, next) ? previous : next));
    };
    const pollSnapshot = async () => {
      if (await service.refresh()) {
        applyEvaluations();
      }
    };
    service.whenReady().then(applyEvaluations);
    const timer = setInterval(pollSnapshot, REFRESH_INTERVAL_MS);
    return () => {
      active = false;
      clearInterval(timer);
    };
  }, [service]);

  return <FeatureFlagContext.Provider value={values}>{children}</FeatureFlagContext.Provider>;
};

/** Returns the live boolean flag value; serves the flag's default until Flipt has settled or when it is unreachable. */
export function useFeatureFlag(flag: BooleanFlag): boolean {
  return useContext(FeatureFlagContext).booleans[flag.key] ?? flag.defaultValue;
}

/** Returns the live variant key; serves the flag's default variant until Flipt has settled or when it is unreachable. */
export function useFeatureVariant(flag: VariantFlag): string {
  return useContext(FeatureFlagContext).variants[flag.key] ?? flag.defaultVariant;
}

/**
 * Returns a stable function that resolves any boolean flag against the current snapshot.
 * Lets non-hook consumers (e.g. the menu builder) read flag values without one hook call per flag.
 */
export function useFeatureFlagEvaluator(): (flag: BooleanFlag) => boolean {
  const booleans = useContext(FeatureFlagContext).booleans;
  return useMemo(() => (flag: BooleanFlag) => booleans[flag.key] ?? flag.defaultValue, [booleans]);
}
